import React from 'react';
import { useParams, useHistory } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { LinearProgress } from '@material-ui/core';
import { Alert } from '@material-ui/lab';

import Box from 'retired/Box';

import { selectUser, selectOrganization } from 'entities/user/selectors';
import { Privileges } from 'utils/privileges';
import { useChallengeQuery, useCommunityTeamsQuery } from 'generated/graphql';

import ChallengeForm from './ChallengeForm';
import { useChallengeMutation } from './useChallengeMutation';
import { ChallengeFormData } from './types';

export const ChallengeFormPage: React.FC = () => {
  const { id: challengeId } = useParams<{ id?: string }>();
  const history = useHistory();
  const user = useSelector(selectUser);
  const organization = useSelector(selectOrganization);

  const isEditing = !!challengeId && challengeId !== 'new';

  if (user && (!user.hasPrivilege(Privileges.CHALLENGES_MANAGE) || !organization?.isCompany())) {
    history.replace('/');
    return null;
  }

  const challengeQuery = useChallengeQuery({
    variables: {
      where: { id: { equals: challengeId! } },
    },
    skip: !isEditing,
  });

  const teamsQuery = useCommunityTeamsQuery({
    variables: {
      where: {
        companyId: { equals: organization?.id },
        type: { equals: 'Team' },
      },
      take: 200,
    },
  });

  const [challengeMutation, mutationResult] = useChallengeMutation(challengeId);

  const challenge = challengeQuery.data?.challenges?.items?.[0];
  const teams = teamsQuery.data?.communities || [];

  const isLoading = challengeQuery.loading || teamsQuery.loading;
  const isSubmitting = mutationResult.loading;

  const handleSubmit = (data: ChallengeFormData) => {
    const payload = {
      ...data,
      id: isEditing ? challengeId : undefined,
    };
    challengeMutation(payload);
  };

  const handleCancel = () => {
    if (isEditing && challengeId) {
      history.push(`/challenges/${challengeId}`);
    } else {
      history.push('/challenges');
    }
  };

  return (
    <>
      {isLoading ? (
        <Box>
          <LinearProgress />
        </Box>
      ) : (
        <>
          {challengeQuery.error && (
            <Alert severity="error">An error occurred when fetching the challenge</Alert>
          )}
          {teamsQuery.error && (
            <Alert severity="error">An error occurred when fetching teams</Alert>
          )}

          {isEditing && !challenge && !challengeQuery.loading && (
            <Alert severity="error">Challenge not found</Alert>
          )}

          {(!isEditing || challenge) && !challengeQuery.error && !teamsQuery.error && (
            <ChallengeForm
              challenge={challenge}
              teams={teams}
              isLoading={isSubmitting}
              onSubmit={handleSubmit}
              onCancel={handleCancel}
            />
          )}
        </>
      )}
    </>
  );
};

export default ChallengeFormPage;
